import React, { useState, useEffect } from 'react';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FarmingReviewFormData, createDefaultFarmingFormData } from '../../types/farmingReview';
import { PMT3LoadingState, PMT3SpreadsheetConfig } from '../../types/pmt3Types';
import { farmingWriteService } from '../../services/farmingWriteService';
import { useFarmingResultsSelection } from '../../context/FarmingResultsSelectionContext';
import { useAuth } from '../../context/AuthContext';
import { pmt2Service } from '../../services/pmt2Service';
import { ChevronDown, ChevronUp, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const STORAGE_KEY = 'pmt3_farming_review_form_data';

const BUNDLE_ACTIONS = [
  'Ignore From Farming Dashboard',
  'Add to Bundle',
  'Remove from Bundle',
  'Increase Bid',
  'Decrease Bid',
  'Pause Campaign',
  'Resume Campaign',
];

export const FarmingReviewForm: React.FC = () => {
  const [formData, setFormData] = useState<FarmingReviewFormData>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...createDefaultFarmingFormData(), ...JSON.parse(saved) };
      }
    } catch (error) {
      console.warn('Failed to load farming form data from localStorage:', error);
    }
    return createDefaultFarmingFormData();
  });

  const [loadingState, setLoadingState] = useState<PMT3LoadingState>({ isLoading: false });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [spreadsheetId, setSpreadsheetId] = useState<string>('');
  const [showOverride, setShowOverride] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const { selectedCompany, selectedProduct } = useFarmingResultsSelection();
  const { displayName } = useAuth();
  const currentASIN = selectedProduct?.id || '';

  // Load farmingResultsSpreadsheetId based on selected company
  useEffect(() => {
    const loadSpreadsheetId = async () => {
      if (!selectedCompany) { setSpreadsheetId(''); return; }
      try {
        const configs = await pmt2Service.getSpreadsheetConfigs();
        const config = configs.find(c => c.companyName === selectedCompany);
        const farmingId = (config as unknown as PMT3SpreadsheetConfig)?.farmingResultsSpreadsheetId;
        if (farmingId) {
          setSpreadsheetId(farmingId);
        } else {
          console.warn(`No Farming Results spreadsheet configured for: ${selectedCompany}`);
          setSpreadsheetId('');
        }
      } catch (error) {
        console.error('Error loading farming spreadsheet config:', error);
        setSpreadsheetId('');
      }
    };
    loadSpreadsheetId();
  }, [selectedCompany]);

  // Save to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.warn('Failed to save farming form data:', error);
    }
  }, [formData]);

  const updateBundleEntry = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      bundleEntry: { ...prev.bundleEntry, [field]: value },
    }));
    setValidationErrors([]);
    setSuccessMessage('');
  };

  const updateOverrideEntry = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      overrideEntry: { ...(prev.overrideEntry || { campaign: '', target: '', overrideBid: null, overrideStatus: '', expirationDate: '' }), [field]: value },
    }));
  };

  const handleSubmit = async () => {
    const validation = farmingWriteService.validateFormClientSide(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    if (!spreadsheetId) {
      setValidationErrors(['No Farming Results spreadsheet configured for this company']);
      return;
    }

    setLoadingState({ isLoading: true, message: 'Submitting farming review...' });
    setValidationErrors([]);
    setSuccessMessage('');

    try {
      const result = await farmingWriteService.completeFarmingReview(
        formData,
        spreadsheetId,
        currentASIN,
        displayName || undefined
      );

      if (result.success) {
        setSuccessMessage(`Review submitted${result.data?.nextASIN ? ` — next ASIN: ${result.data.nextASIN}` : ''}`);
        setFormData(createDefaultFarmingFormData());
        setShowOverride(false);
        setShowSettings(false);
      } else {
        setValidationErrors([result.error || 'Submission failed']);
      }
    } catch (error: any) {
      setValidationErrors([error.message || 'Failed to submit farming review']);
    } finally {
      setLoadingState({ isLoading: false });
    }
  };

  const handlePasteNotes = async () => {
    if (!formData.notes.trim() || !spreadsheetId) return;

    setLoadingState({ isLoading: true, message: 'Pasting notes...' });
    try {
      const result = await farmingWriteService.pasteFarmingNotes(formData.notes, spreadsheetId);
      if (result.success) {
        setSuccessMessage('Notes pasted to sheet');
      }
    } catch (error: any) {
      setValidationErrors([error.message || 'Failed to paste notes']);
    } finally {
      setLoadingState({ isLoading: false });
    }
  };

  const handleNextASIN = async () => {
    if (!spreadsheetId) return;

    setLoadingState({ isLoading: true, message: 'Navigating...' });
    try {
      const result = await farmingWriteService.nextFarmingASIN(spreadsheetId, currentASIN);
      if (result.success) {
        setSuccessMessage(`Navigated to ${result.data?.nextASIN || 'next ASIN'}`);
      }
    } catch (error: any) {
      setValidationErrors([error.message || 'Failed to navigate']);
    } finally {
      setLoadingState({ isLoading: false });
    }
  };

  const handleClear = () => {
    setFormData(createDefaultFarmingFormData());
    setValidationErrors([]);
    setSuccessMessage('');
    setShowOverride(false);
    setShowSettings(false);
    localStorage.removeItem(STORAGE_KEY);
  };

  const isDisabled = loadingState.isLoading || !spreadsheetId;

  return (
    <div className="px-4 py-2 space-y-3 overflow-y-auto max-h-[60vh]">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Farming Review</h3>

      {/* No spreadsheet warning */}
      {selectedCompany && !spreadsheetId && (
        <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs text-yellow-700 dark:text-yellow-400">
          No Farming Results spreadsheet configured for {selectedCompany}.
        </div>
      )}

      {/* Current ASIN indicator */}
      {currentASIN && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          ASIN: <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{currentASIN}</span>
        </div>
      )}

      {/* Bundle Disposition (required) */}
      <div className="space-y-2">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
          Bundle Action *
        </label>
        <Select
          value={formData.bundleEntry.action}
          onValueChange={(val) => updateBundleEntry('action', val)}
          disabled={isDisabled}
        >
          <SelectTrigger className="w-full text-xs h-8">
            <SelectValue placeholder="Select action..." />
          </SelectTrigger>
          <SelectContent>
            {BUNDLE_ACTIONS.map((action) => (
              <SelectItem key={action} value={action} className="text-xs">
                {action}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="number"
          placeholder="Value"
          value={formData.bundleEntry.value ?? ''}
          onChange={(e) => updateBundleEntry('value', e.target.value ? Number(e.target.value) : null)}
          disabled={isDisabled}
          className="h-8 text-xs"
        />
      </div>

      {/* Override Bid (collapsible) */}
      <div>
        <button
          onClick={() => setShowOverride(!showOverride)}
          className="flex items-center text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          disabled={isDisabled}
        >
          {showOverride ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
          Override Bid (optional)
        </button>
        {showOverride && (
          <div className="mt-2 space-y-2 pl-2 border-l-2 border-gray-200 dark:border-gray-700">
            <Input placeholder="Campaign" value={formData.overrideEntry?.campaign || ''} onChange={(e) => updateOverrideEntry('campaign', e.target.value)} disabled={isDisabled} className="h-8 text-xs" />
            <Input placeholder="Target" value={formData.overrideEntry?.target || ''} onChange={(e) => updateOverrideEntry('target', e.target.value)} disabled={isDisabled} className="h-8 text-xs" />
            <Input type="number" placeholder="Bid" value={formData.overrideEntry?.overrideBid ?? ''} onChange={(e) => updateOverrideEntry('overrideBid', e.target.value ? Number(e.target.value) : null)} disabled={isDisabled} className="h-8 text-xs" />
            <Input placeholder="Status" value={formData.overrideEntry?.overrideStatus || ''} onChange={(e) => updateOverrideEntry('overrideStatus', e.target.value)} disabled={isDisabled} className="h-8 text-xs" />
            <Input type="date" placeholder="Expiration" value={formData.overrideEntry?.expirationDate || ''} onChange={(e) => updateOverrideEntry('expirationDate', e.target.value)} disabled={isDisabled} className="h-8 text-xs" />
          </div>
        )}
      </div>

      {/* Settings (collapsible) */}
      <div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="flex items-center text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          disabled={isDisabled}
        >
          {showSettings ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
          Settings (optional)
        </button>
        {showSettings && (
          <div className="mt-2 space-y-2 pl-2 border-l-2 border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">$/CST Override</label>
              <Input type="number" placeholder="$/CST Override" value={formData.cstOverride ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, cstOverride: e.target.value ? Number(e.target.value) : null }))} disabled={isDisabled} className="h-8 text-xs" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="limitMaxBid" checked={formData.limitToMaxBid ?? false} onChange={(e) => setFormData(prev => ({ ...prev, limitToMaxBid: e.target.checked }))} disabled={isDisabled} className="h-3 w-3" />
              <label htmlFor="limitMaxBid" className="text-xs text-gray-600 dark:text-gray-400">Limit to Max Bid</label>
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">PPC Status</label>
              <Input placeholder="PPC Status" value={formData.newPpcStatus} onChange={(e) => setFormData(prev => ({ ...prev, newPpcStatus: e.target.value }))} disabled={isDisabled} className="h-8 text-xs" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="pauseFarming" checked={formData.pauseFarming ?? false} onChange={(e) => setFormData(prev => ({ ...prev, pauseFarming: e.target.checked }))} disabled={isDisabled} className="h-3 w-3" />
              <label htmlFor="pauseFarming" className="text-xs text-gray-600 dark:text-gray-400">Pause Farming</label>
            </div>
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
        <Textarea
          placeholder="Enter notes..."
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          disabled={isDisabled}
          className="text-xs min-h-[60px]"
        />
        {formData.notes.trim() && (
          <Button variant="ghost" size="sm" onClick={handlePasteNotes} disabled={isDisabled} className="mt-1 text-xs h-6 px-2">
            Paste Notes to Sheet
          </Button>
        )}
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          {validationErrors.map((err, i) => (
            <div key={i} className="flex items-center gap-1 text-xs text-red-700 dark:text-red-400">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              {err}
            </div>
          ))}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3 text-green-600 dark:text-green-400 flex-shrink-0" />
          <span className="text-xs text-green-700 dark:text-green-400">{successMessage}</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col gap-2">
        <Button
          onClick={handleSubmit}
          disabled={isDisabled || !formData.bundleEntry.action}
          className="w-full h-8 text-xs"
        >
          {loadingState.isLoading ? (
            <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> {loadingState.message}</>
          ) : (
            'Submit Review'
          )}
        </Button>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleNextASIN} disabled={isDisabled} className="flex-1 h-7 text-xs">
            Next ASIN
          </Button>
          <Button variant="ghost" size="sm" onClick={handleClear} disabled={loadingState.isLoading} className="flex-1 h-7 text-xs">
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FarmingReviewForm;
